import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// Switch to RTDB hook
import useMarketDataRTDB from './hooks/useMarketDataRTDB';
import TrendingTeams from './TrendingTeams';
import MarketStats from './MarketStats';
import ConfidenceTrendChart from './ConfidenceTrendChart';
import GameMarketView from './GameMarketView';
import BettingLeaderboard from '../../leaderboards/BettingLeaderboard';
import { getBrazilianStatus } from '../../../utils/detectBrazil';
import { marketAnalyticsPtBR } from '../../mappings/marketExchange/marketAnalytics.pt-br';

const MarketAnalytics = () => {
  const navigate = useNavigate();
  const isBrazilian = getBrazilianStatus();
  const t = (key) => isBrazilian && marketAnalyticsPtBR[key] ? marketAnalyticsPtBR[key] : key;

  const [timeRange, setTimeRange] = useState('7d'); // Default to 7 days
  const [selectedSport, setSelectedSport] = useState('ALL'); // Default to all sports
  const { marketData, loading, error } = useMarketDataRTDB(timeRange, selectedSport);

  // Dashboard navigation pills
  const dashboardRoutes = [
    { label: t('NFL'), route: '/nfl' },
    { label: t('NCAAF'), route: '/ncaaf' },
    { label: t('MLB'), route: '/mlb' },
    { label: t('MMA'), route: '/mma' },
    { label: t('FOOTBALL'), route: '/football' },
    { label: t('VOLLEYBALL'), route: '/volleyball' },
    { label: t('SPORTS HUB'), route: '/sports-hub' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8">
          <div className="flex items-center justify-center h-96">
            <div className="text-zinc-500 uppercase tracking-wider text-xs font-bold animate-pulse">
              {t('Loading market data...')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black">
        <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8">
          <div className="flex items-center justify-center h-96">
            <div className="text-red-500 uppercase tracking-wider text-xs font-bold border border-red-500/20 p-6">
              {t('Error loading market data:')} {error.message}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-7xl mx-auto">
        {/* Mobile spacing */}
        <div className="h-10 md:hidden" />

        {/* Navigation Pills and CTA */}
        <div className="border-b border-zinc-900 bg-black/95 backdrop-blur sticky top-0 z-40">
          <div className="px-4 md:px-6 lg:px-8 py-3 md:py-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              {/* Dashboard Pills */}
              <div className="flex gap-[1px] bg-zinc-900 p-[1px] overflow-x-auto scrollbar-hide">
                {dashboardRoutes.map((item) => (
                  <button
                    key={item.route}
                    className="px-3 py-2 md:px-4 md:py-2.5 bg-black hover:bg-zinc-900 text-zinc-500 hover:text-white
                             text-[10px] md:text-xs font-black uppercase tracking-wider whitespace-nowrap
                             transition-all duration-200 active:bg-violet-600/20 active:text-violet-400"
                    onClick={() => navigate(item.route)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {/* CTA */}
              <div className="flex items-center gap-2 md:gap-3">
                <span className="hidden md:block text-zinc-600 text-[10px] font-black uppercase tracking-wider">
                  {t('WANT FRESH PREDICTIONS?')}
                </span>
                <button
                  className="flex-1 md:flex-initial px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white
                           text-[10px] md:text-xs font-black uppercase tracking-wider transition-all
                           duration-200 active:translate-y-px"
                  onClick={() => navigate('/sports-hub')}
                >
                  {t('QUICKPICK BOARDS')} →
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6">
          {/* Header */}
          <div className="border border-zinc-900 bg-zinc-950/50 mb-4 md:mb-6">
            <div className="p-4 md:p-6 space-y-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <h1 className="text-xl md:text-2xl font-black uppercase tracking-tight text-white">
                  {t('PickStar Exchange [Beta]')}
                </h1>
                <div className="flex items-center gap-2 px-3 py-1.5 border border-zinc-800">
                  <div className="w-2 h-2 bg-violet-500 animate-pulse" />
                  <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400">
                    {t('LIVE MARKET')}
                  </span>
                </div>
              </div>

              {/* Time Controls */}
              <div className="flex gap-0 border border-zinc-900 overflow-x-auto scrollbar-hide">
                {['1h', '24h', '3d', '7d', '14d', '30d'].map((range) => (
                  <button
                    key={range}
                    className={`px-3 md:px-5 py-2 md:py-2.5 text-[10px] md:text-xs font-black uppercase tracking-wider
                              transition-all duration-200 whitespace-nowrap border-r border-zinc-900 last:border-r-0
                              ${timeRange === range
                                ? 'bg-violet-600 text-white border-b-2 border-b-violet-400'
                                : 'bg-black hover:bg-zinc-900 text-zinc-500 hover:text-white'}`}
                    onClick={() => setTimeRange(range)}
                  >
                    {t(range.toUpperCase())}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <MarketStats stats={marketData.stats} />

          {/* Glossary */}
          <div className="border border-zinc-900 bg-zinc-950/30 p-3 md:p-4 mb-4 md:mb-6">
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[10px] md:text-xs">
              <span className="text-zinc-600">
                <span className="text-zinc-400 font-bold">QPA:</span> Quick Pick Activation
              </span>
              <span className="text-zinc-800 hidden md:inline">•</span>
              <span className="text-zinc-600">
                <span className="text-zinc-400 font-bold">CONF:</span> AI Confidence
              </span>
              <span className="text-zinc-800 hidden md:inline">•</span>
              <span className="text-zinc-600">
                <span className="text-zinc-400 font-bold">POOL:</span> Coins Bet
              </span>
              <span className="text-zinc-800 hidden md:inline">•</span>
              <span className="text-zinc-600">
                <span className="text-zinc-400 font-bold">ODDS:</span> Payout
              </span>
              <span className="text-zinc-800 hidden md:inline">•</span>
              <span className="text-zinc-600">
                <span className="text-zinc-400 font-bold">CROWD:</span> Consensus
              </span>
            </div>
            <div className="mt-3 pt-3 border-t border-zinc-900 text-center text-[9px] md:text-[10px] text-zinc-700">
              Only star coins stakes. All payouts will be after 12am ET. For any questions email support@pickstarai.com.
            </div>
          </div>

          {/* Game Market View */}
          <div className="border border-zinc-900 bg-zinc-950/50 p-4 md:p-6 mb-4 md:mb-6">
            <GameMarketView gameMarkets={marketData.gameMarkets} selectedSport={selectedSport} />
          </div>

          {/* Confidence Chart */}
          <div className="border border-zinc-900 bg-zinc-950/50 p-4 md:p-6 mb-4 md:mb-6">
            <ConfidenceTrendChart timeSeriesData={marketData.confidenceTimeSeries} />
          </div>

          {/* Trending Teams */}
          <div className="border border-zinc-900 bg-zinc-950/50 p-4 md:p-6 mb-4 md:mb-6">
            <h2 className="text-xs font-black uppercase tracking-wider text-zinc-500 mb-4">
              {t('TRENDING TEAMS')}
            </h2>
            <TrendingTeams teams={marketData.trendingTeams} />
          </div>

          {/* Leaderboard */}
          <div className="border border-zinc-900 bg-zinc-950/50 p-4 md:p-6">
            <BettingLeaderboard />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketAnalytics;